import { COMMAND_CENTER_ROOT } from '../commandCenterRoutes'
import { useState } from 'react'
import CommsFrame, { StateBlock } from './CommsShell'
import { StatusBadge } from '../operations'
import { useContextMenu, type ContextMenuAction } from '../shell/ContextMenu'
import { OpenIcon, CheckIcon, WarnIcon, CopyIcon, EmailIcon, DownloadIcon, ScheduleIcon, TrashIcon } from '../shell/icons'
import type { TeamMember, TeamTask } from './commsData'

/**
 * TeamHome — /square/command-center/team.
 *
 * PHASE_2I: presentational. Roster (sq_team_members), active tasks + blockers
 * (sq_team_tasks). No fabricated members or tasks — honest empty/loading/error/
 * auth states. Custom roles are DEFERRED (v1 uses a fixed role enum).
 *
 * PHASE_2M P5: every task row is clickable (opens the detail) AND carries the
 * shared right-click context menu — Mark Done / Mark Blocked are REAL audited
 * mutations via onTaskAction; Copy/Email/Download are real client actions;
 * Schedule is INTEGRATION_DEFERRED; Delete is BACKEND_DEFERRED (no task
 * delete API — never faked).
 *
 * Chat #2 conversion: presentation lives in comms-surface.css. Task status is a
 * feature chip (.sqv-comms-chip[data-status]) with the exact amber/cyan/red/
 * green palette; no hex/inline color literals remain here.
 */
export interface TeamHomeProps {
  members: TeamMember[]
  tasks: TeamTask[]
  membersLoading: boolean
  membersError: string | null
  tasksLoading: boolean
  tasksError: string | null
  authRequired: boolean
  canManage?: boolean
  onAddMember?: (displayName: string) => void | Promise<unknown>
  onMessageMember?: (userId: string) => void | Promise<unknown>
  onCreateTask?: (title: string) => void | Promise<unknown>
  onOpenTask?: (id: string) => void
  /** REAL quick mutations from the context menu (update_status / set_blocker). */
  onTaskAction?: (id: string, action: 'done' | 'blocked') => void | Promise<unknown>
  onOpenRoles?: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onNavTop?: (to: string) => void
}

export default function TeamHome(props: TeamHomeProps) {
  const { members, tasks, membersLoading, membersError, tasksLoading, tasksError, authRequired, canManage, onAddMember, onMessageMember, onCreateTask, onOpenTask, onTaskAction, onOpenRoles, onHome, onBackCommandCenter, onNavTop } = props
  const [memberName, setMemberName] = useState('')
  const [taskTitle, setTaskTitle] = useState('')
  const menu = useContextMenu()
  const blockers = tasks.filter((t) => t.status === 'blocked')
  const active = tasks.filter((t) => t.status !== 'done')

  const taskMenuActions = (t: TeamTask): ContextMenuAction[] => {
    const taskPath = `${COMMAND_CENTER_ROOT}/team/tasks/${t.id}`
    const appUrl = typeof window !== 'undefined' ? `${window.location.origin}${taskPath}` : taskPath
    return [
      { id: 'open', label: 'Open', icon: <OpenIcon size={13} />, onSelect: () => onOpenTask?.(t.id) },
      { id: 'done', label: 'Mark Done', icon: <CheckIcon size={13} />, onSelect: () => void onTaskAction?.(t.id, 'done'), hint: 'Real audited status change' },
      { id: 'blocked', label: 'Mark Blocked', icon: <WarnIcon size={13} />, onSelect: () => void onTaskAction?.(t.id, 'blocked'), hint: 'Real audited status change — add the reason in the detail' },
      { id: 'copy', label: 'Copy Link', icon: <CopyIcon size={13} />, onSelect: () => { void navigator.clipboard?.writeText(appUrl) } },
      { id: 'email', label: 'Email', icon: <EmailIcon size={13} still />, onSelect: () => { window.location.href = `mailto:?subject=${encodeURIComponent(`Task — ${t.title}`)}&body=${encodeURIComponent(appUrl)}` }, hint: 'Opens your mail client' },
      {
        id: 'download', label: 'Download JSON', icon: <DownloadIcon size={13} />, hint: 'Real export of the saved record',
        onSelect: () => {
          const blob = new Blob([JSON.stringify(t, null, 2)], { type: 'application/json' })
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob); a.download = `task-${t.id.slice(0, 8)}.json`; a.click(); URL.revokeObjectURL(a.href)
        },
      },
      { id: 'schedule', label: 'Schedule', icon: <ScheduleIcon size={13} />, deferred: 'INTEGRATION_DEFERRED' },
      { id: 'delete', label: 'Delete', icon: <TrashIcon size={13} />, danger: true, deferred: 'BACKEND_DEFERRED', hint: 'No task delete API — never faked' },
    ]
  }

  const TaskRow = ({ t }: { t: TeamTask }) => (
    <button
      type="button"
      onClick={() => onOpenTask?.(t.id)}
      onContextMenu={(e) => menu.open(e, taskMenuActions(t), `Task — ${t.title}`)}
      className="sqv-comms-tile sqv-comms-rowbtn"
      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
    >
      <span className="sqv-comms-chip" data-status={t.status}>{t.status.replace('_', ' ').toUpperCase()}</span>
      <span style={{ fontSize: 13, fontWeight: 700 }}>{t.title}</span>
      <span className="sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 9.5, marginLeft: 'auto' }}>{t.priority}</span>
    </button>
  )

  const center = (
    <div className="sqv-surface-comms" style={{ gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Team roster</h3>
        {authRequired || membersLoading || membersError || members.length === 0 ? (
          <StateBlock loading={membersLoading} error={membersError} authRequired={authRequired} empty={members.length === 0} emptyLabel="No team members yet — add the first member from the panel on the right. (No fabricated teammates.)" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {members.map((m) => (
              <div key={m.id} className="sqv-comms-tile" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="sqv-comms-c-1" style={{ fontSize: 13, fontWeight: 700 }}>{m.display_name || m.user_id?.slice(0, 8) || 'Member'}</span>
                <span className="sqv-comms-rolebadge">{m.role.toUpperCase()}</span>
                {m.status !== 'active' && <span className="sqv-comms-statusflag">{m.status}</span>}
                {m.user_id && onMessageMember && <button type="button" onClick={() => onMessageMember(m.user_id!)} title="Open a direct message" className="sqv-comms-ghostbtn" style={{ marginLeft: 'auto' }}>Message</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Active tasks</h3>
        {authRequired || tasksLoading || tasksError || active.length === 0 ? (
          <StateBlock loading={tasksLoading} error={tasksError} authRequired={authRequired} empty={active.length === 0} emptyLabel="No active tasks — create one below or from a channel." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>{active.map((t) => <TaskRow key={t.id} t={t} />)}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
          <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && taskTitle.trim()) { onCreateTask?.(taskTitle.trim()); setTaskTitle('') } }} placeholder="New task title…" aria-label="New task title" disabled={authRequired} className="sqv-comms-input" style={{ flex: 1 }} />
          <button type="button" onClick={() => { if (taskTitle.trim()) { onCreateTask?.(taskTitle.trim()); setTaskTitle('') } }} disabled={authRequired} className="sqv-comms-btn">Add task</button>
        </div>
      </div>

      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Blockers</h3>
        {authRequired || tasksLoading || tasksError || blockers.length === 0 ? (
          // Surface the error too — a failed task load must NOT read as a
          // confident "no blockers" all-clear.
          <StateBlock loading={tasksLoading} error={tasksError} authRequired={authRequired} empty={blockers.length === 0} emptyLabel="No blockers — nothing is flagged blocked right now." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>{blockers.map((t) => <TaskRow key={t.id} t={t} />)}</div>
        )}
      </div>
    </div>
  )

  const rightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Add team member</h3>
        {canManage ? (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={memberName} onChange={(e) => setMemberName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && memberName.trim()) { onAddMember?.(memberName.trim()); setMemberName('') } }} placeholder="Member name…" aria-label="New member name" className="sqv-comms-input" style={{ flex: 1 }} />
              <button type="button" onClick={() => { if (memberName.trim()) { onAddMember?.(memberName.trim()); setMemberName('') } }} className="sqv-comms-btn">Add</button>
            </div>
            <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10.5 }}>Adds a real roster row (audited). Invited shells start as "invited" until a user is linked.</p>
          </>
        ) : (
          <p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 11.5, lineHeight: 1.6 }}>Adding members requires an admin/owner role.</p>
        )}
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Roles</h3>
        <p className="sqv-comms-mb9 sqv-comms-c-3" style={{ fontSize: 11.5, lineHeight: 1.6 }}>v1 roles are fixed: owner · admin · member · viewer.</p>
        <div style={{ marginBottom: 9 }}><StatusBadge status="BACKEND_DEFERRED" size="sm" /></div>
        <button type="button" onClick={onOpenRoles} className="sqv-comms-btn" style={{ width: '100%', textAlign: 'left' }}>Custom roles (deferred) →</button>
      </div>
    </div>
  )

  return (
    <>
      <CommsFrame
        activeTop="team"
        surface="team-home"
        breadcrumb={['COMMAND CENTER', 'TEAM']}
        title="Team"
        onHome={onHome}
        onBackCommandCenter={onBackCommandCenter}
        onNavTop={onNavTop}
        center={center}
        rightRail={rightRail}
      />
      {menu.element}
    </>
  )
}
